import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import BreederProfileCurrentPuppiesSkeleton from 'dpl/components/BreederProfile/Show/skeletons/BreederProfileCurrentPuppiesSkeleton';
import { ANALYTICS_VIEW_FEATURE_LOCATIONS } from 'dpl/constants/analytics';
import SectionViewTracker from 'dpl/common/components/SectionViewTracker';
import { SearchPuppiesNearYouCard } from 'dpl/components/BreederProfile/Show/SearchPuppiesPaths';
import BreederProfileLitterPuppyCard from 'dpl/components/BreederProfile/Show/BreederProfileLitterPuppyCard';
import useLitters from 'dpl/components/BreederProfile/mobile/hooks/useLitters';
import useToggle from 'dpl/shared/hooks/useToggle';
import Button from 'dpl/common/design_system/Button';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import { groupBy, uniqBy } from 'dpl/shared/utils';
import FilterPills from 'dpl/common/components/FilterPills';
import { LITTER_STATES } from 'dpl/shared/constants/shared';

export const SECTION_ID = 'puppies-section';
const IS_LT_MD = isLessThanBreakpoint('md');
const COLLAPSED_PUPPIES_COUNT = IS_LT_MD ? 3 : 8;

export default function BreederProfileCurrentPuppiesSectionContainer() {
  const {
    breeds,
    breederProfileId,
    analyticsPageName,
    isSearchEngineReferral
  } = useSelector(({ server }) => server);

  const {
    isFetchingCurrentLitters,
    sortedAvailablePuppies,
    availableAdultDogs
  } = useLitters({ status: LITTER_STATES.BORN });

  const [selectedFilterIdx, setSelectedFilterIdx] = useState(0);
  const [isCollapsed, toggleIsCollapsed] = useToggle(true);

  const availablePuppiesAndDogs = [
    ...sortedAvailablePuppies,
    ...availableAdultDogs
  ];

  const hasNoPuppies = availablePuppiesAndDogs.length === 0;

  if (isFetchingCurrentLitters) {
    return <BreederProfileCurrentPuppiesSkeleton />;
  }

  if (hasNoPuppies) {
    return null;
  }

  const dogsByBreed = groupBy(availablePuppiesAndDogs, 'breed_name');
  const filteredDogs =
    dogsByBreed[Object.keys(dogsByBreed)[selectedFilterIdx]] || [];
  const breedFilters = uniqBy(availablePuppiesAndDogs, 'breed_name').map(
    dog => {
      return {
        label: dog.breed_name,
        photo_url: dog.breed_cover_photo
      };
    }
  );

  const selectedBreedName = breedFilters[selectedFilterIdx]?.label;
  const selectedBreed = breeds.find(breed => breed.name === selectedBreedName);

  const isCollapsible = filteredDogs.length > COLLAPSED_PUPPIES_COUNT;

  const listings = isCollapsed
    ? filteredDogs.slice(0, COLLAPSED_PUPPIES_COUNT)
    : filteredDogs;

  function handleBreedClick(idx) {
    setSelectedFilterIdx(idx);
  }

  return (
    <SectionViewTracker
      name={analyticsPageName}
      category="breeder-profile-litters-and-puppies"
      properties={{ breeder_profile_id: breederProfileId }}
    >
      <section
        id={SECTION_ID}
        className="BreederProfileCurrentPuppiesSection mv8 mv20-md"
      >
        <div className="container-xxl">
          <h3 className="mb6 mb8-md font-24 md:font-40 lh-title">
            {availableAdultDogs.length > 0
              ? 'Available puppies and older dogs'
              : 'Available puppies'}
          </h3>
          {breedFilters.length > 1 && (
            <FilterPills
              className="mb6 mb8-md"
              items={breedFilters}
              onFilterClick={handleBreedClick}
              selectedFilterIdx={selectedFilterIdx}
            />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-16 gap-y-24 md:gap-y-32">
            {listings.map((puppy, idx) => (
              <div key={puppy.id}>
                <BreederProfileLitterPuppyCard
                  puppy={puppy}
                  breederProfileId={breederProfileId}
                  analytics={{
                    position: idx + 1,
                    view_feature_location:
                      ANALYTICS_VIEW_FEATURE_LOCATIONS.BREEDER_PROFILE_PUPPIES_CAROUSEL
                  }}
                />
              </div>
            ))}
            {isSearchEngineReferral &&
              ((isCollapsible && !isCollapsed) || !isCollapsible) && (
                <div key="search-puppies-card">
                  <SearchPuppiesNearYouCard breed={selectedBreed} />
                </div>
              )}
          </div>
          {isCollapsible && (
            <Button
              className="font-16 stone-700 w-100 w-auto-ns mt4 mt8-md"
              variant="outline"
              size="default"
              onClick={toggleIsCollapsed}
            >
              {isCollapsed ? 'Show all puppies' : 'Show less puppies'}
            </Button>
          )}
        </div>
      </section>
    </SectionViewTracker>
  );
}
